import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircleIcon } from '@solar-icons/react/bold/check-circle'
import { CloseCircleIcon } from '@solar-icons/react/bold/close-circle'
import { MinusCircleIcon } from '@solar-icons/react/linear/minus-circle'
import { PlayCircleIcon } from '@solar-icons/react/linear/play-circle'
import { Badge, Button, Panel, Spinner } from '@/components/ui'
import { useDiagnostics } from './useDiagnostics'
import type { ProbeStatus } from './useDiagnostics'
import { formatDuration } from '@/lib/format/cells'
import { QUERY_ROUTE_PATHS } from '@/lib/api/queryApi'
import { joinUrl } from '@/lib/api'
import { useSettingsStore } from '@/stores/settingsStore'

function StatusIcon({ status }: { status: ProbeStatus }) {
  if (status === 'running') return <Spinner className="text-accent" />
  if (status === 'pass') return <CheckCircleIcon size={16} className="text-success" />
  if (status === 'fail') return <CloseCircleIcon size={16} className="text-danger" />
  return <MinusCircleIcon size={16} strokeWidth={1.75} className="text-subtle" />
}

export function DiagnosticsView() {
  const { results, isRunning, run, resolvedUrls } = useDiagnostics()

  const baseUrl = useSettingsStore((state) => state.baseUrl)
  const route = useSettingsStore((state) => state.route)
  const timeoutMs = useSettingsStore((state) => state.requestTimeoutMs)
  const hasToken = useSettingsStore((state) => Boolean(state.bearerToken))

  // Probe once on mount so the page is useful before anyone clicks anything.
  useEffect(() => {
    void run()
    // Intentionally mount-only; re-running on every context change would fire
    // a burst of requests while someone types a URL in Settings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const passed = results.filter((result) => result.status === 'pass').length
  const failed = results.filter((result) => result.status === 'fail').length

  return (
    <div className="scrollbar-thin h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-6">
        <Panel
          title="Endpoint probes"
          description="Read-only checks against the health routes. No query is submitted, so nothing here consumes LLM tokens."
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={() => void run()}
              disabled={isRunning}
              icon={isRunning ? <Spinner /> : <PlayCircleIcon size={14} strokeWidth={1.75} />}
            >
              {isRunning ? 'Running' : 'Run probes'}
            </Button>
          }
          bodyClassName="p-0"
        >
          <ul>
            {results.map((result) => (
              <li
                key={result.id}
                className="flex items-start gap-3 border-b border-line px-5 py-3.5 last:border-b-0"
              >
                <span className="mt-0.5 shrink-0">
                  <StatusIcon status={result.status} />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-fg">
                      {result.label}
                    </span>
                    <code
                      className="font-mono text-[11px] text-subtle"
                      title={resolvedUrls[result.id]}
                    >
                      {result.path}
                    </code>
                    {typeof result.durationMs === 'number' &&
                      result.status !== 'running' && (
                        <Badge>{formatDuration(result.durationMs)}</Badge>
                      )}
                  </div>

                  <p className="mt-0.5 text-xs leading-relaxed text-muted">
                    {result.description}
                  </p>

                  {result.status === 'pass' && result.detail && (
                    <p className="mt-1.5 font-mono text-[11px] text-success">
                      {result.detail}
                    </p>
                  )}

                  {result.status === 'fail' && result.error && (
                    <p className="mt-1.5 text-xs leading-relaxed text-danger">
                      {result.error}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel
          title="Client configuration"
          description="What this browser will send with the next query."
        >
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <div>
              <dt className="text-[11px] tracking-wide text-subtle uppercase">
                Base URL
              </dt>
              <dd className="mt-0.5 font-mono text-xs break-all text-fg">
                {baseUrl || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] tracking-wide text-subtle uppercase">
                Query endpoint
              </dt>
              <dd className="mt-0.5 font-mono text-xs break-all text-fg">
                {baseUrl ? joinUrl(baseUrl, QUERY_ROUTE_PATHS[route]) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] tracking-wide text-subtle uppercase">
                Authorization
              </dt>
              <dd className="mt-0.5 text-xs text-fg">
                {route === 'production'
                  ? hasToken
                    ? 'Bearer token set'
                    : 'Missing — queries will fail'
                  : 'Not sent on the test route'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] tracking-wide text-subtle uppercase">
                Request timeout
              </dt>
              <dd className="mt-0.5 text-xs text-fg">
                {Math.round(timeoutMs / 1000)}s
              </dd>
            </div>
          </dl>

          <div className="mt-4 border-t border-line pt-3">
            <p className="text-xs leading-relaxed text-muted">
              Summary: {passed} passed, {failed} failed.{' '}
              {failed > 0 && (
                <>
                  Check the base URL in{' '}
                  <Link
                    to="/settings"
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    Settings
                  </Link>{' '}
                  and confirm the API process is running.
                </>
              )}
            </p>
          </div>
        </Panel>
      </div>
    </div>
  )
}
